import { useState, useEffect } from 'react';
import { useApolloClient } from '@apollo/react-hooks';
import { StyledIssuesListContainer } from './styled/IssuesList.styled';
import { Issue, IssueListQuery, IssuesListResponse } from './queries/issues-list.queries.types';

import { GetIssueList } from './queries/issues-list.queries';

import IssuesListHeader from './components/IssuesListHeader';
import IssuesFilterBar from './components/IssuesListFilterBar';
import IssuesListTable from './components/IssuesListTable';
import GraphQlErrorBoundary from 'components/layout/ErrorBoundary';
import Loading from 'components/layout/Loading';
import { ApolloError } from 'apollo-client';
import { ObservationStatusEnum } from 'shared/enums/ObservationStatus.enum';
import { useTranslation } from 'react-i18next';

export const IssuesList = () => {
   const client = useApolloClient();
   const [loading, setLoading] = useState<boolean>(true);
   const [loadingError, setLoadingError] = useState<ApolloError>();
   const [filterDataLoaded, setFilterDataLoaded] = useState<boolean>(false);

   const [showFilterBar, setShowFilterBar] = useState<boolean>(false);

   const defaultFilter: IssueListQuery = {
      dateFrom: undefined,
      dateTo: undefined,
      observationCategoryId: [],
      concernLevel: [],
      observationStatus: [ObservationStatusEnum.Open, ObservationStatusEnum.Closed],
      depotPcId: [],
      divisionId: [],
      regionId: [],
      sHEQManagerId: []
   };

   const [issueRequest, setIssueRequest] = useState<IssueListQuery>(defaultFilter);

   const [issueData, setIssueData] = useState<Issue[]>();
   const { t } = useTranslation();

   const toggleFilterBar = () => {
      setShowFilterBar(!showFilterBar);
   };

   useEffect(() => {
      loadIssueData();
   }, [issueRequest]);

   const resetIssueData = () => {
      setIssueRequest(defaultFilter);
   };

   const loadIssueData = async () => {
      setLoading(true);

      try {
         let result = await client.query<IssuesListResponse>({
            query: GetIssueList,
            variables: {
               request: issueRequest
            },
            fetchPolicy: 'network-only'
         });

         if (result?.data?.issueRecords != null) {
            setIssueData(result.data.issueRecords);
         } else {
            setIssueData(undefined);
         }

         result?.data?.issueRecords.forEach(data => {
            if (data.offSiteLocation != null) {
               data.offSiteLocationDescription = data.offSiteLocation.locationDescription;
               data.offSiteLocationCustomerSite = data.offSiteLocation.customerSiteName;
            } else {
               data.offSiteLocationDescription = ' ';
               data.offSiteLocationCustomerSite = ' ';
            }

            if (data.depotPcName == null) {
               data.depotPcName = ' ';
            }
         });

         if (filterDataLoaded === false) {
            setFilterDataLoaded(true);
         }
      } catch (e: any) {
         setLoadingError(e);
      } finally {
         setLoading(false);
      }
   };

   return (
      <StyledIssuesListContainer>
         <IssuesListHeader toggleFilterBar={toggleFilterBar} />
         <IssuesFilterBar
            showFilterBar={showFilterBar}
            toggleFilterBar={toggleFilterBar}
            issueData={issueData}
            filterDataLoaded={filterDataLoaded}
            filterRequest={issueRequest}
            setFilterRequest={setIssueRequest}
            resetFilter={resetIssueData}
         />

         <GraphQlErrorBoundary error={loadingError}>
            <Loading isLoading={loading} message={"Loading Issues..."}>
               <IssuesListTable data={issueData} />
            </Loading>
         </GraphQlErrorBoundary>
      </StyledIssuesListContainer>
   );
};
